import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CourseShape, CreateCourseRequest } from '@eduborg/shared';
import { api } from '@/lib/api';

const COURSES_KEY = ['courses', 'list'] as const;

/**
 * Fetch the list of courses visible to the current user. The api Worker
 * applies visibility rules (creator + staff + super_admin); the client just
 * renders whatever comes back.
 */
export function useCoursesQuery(token: string) {
  return useQuery({
    queryKey: COURSES_KEY,
    queryFn: () => api.courses.list(token),
    staleTime: 30_000,
    enabled: token.length > 0,
  });
}

/**
 * Create-course mutation. On success we optimistically prepend to the cached
 * list so the new card appears instantly; on error we let TanStack roll back.
 */
export function useCreateCourseMutation(token: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCourseRequest) => api.courses.create(token, body),
    onSuccess: ({ course }) => {
      queryClient.setQueryData<{ courses: CourseShape[] }>(COURSES_KEY, (prev) => {
        if (!prev) return { courses: [course] };
        return { courses: [course, ...prev.courses] };
      });
    },
  });
}
